import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import {
  TextB, TextItalic, ListBullets, ListNumbers, Quotes, LinkSimple,
  TextHOne, TextHTwo, Image as ImgIcon, ArrowUUpLeft, ArrowUUpRight,
} from "@phosphor-icons/react";
import { uploadImage } from "./api";
import { toast } from "sonner";

const Btn = ({ active, onClick, title, children, "data-testid": tid }) => (
  <button
    type="button"
    title={title}
    data-testid={tid}
    onClick={onClick}
    className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
      active ? "bg-[#0F2A6E] text-white" : "text-[#1C2B3A] hover:bg-slate-100"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Write the article…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false }),
      Image.configure({ HTMLAttributes: { class: "rounded-lg my-3 max-w-full h-auto" } }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-slate max-w-none min-h-[280px] focus:outline-none p-4 text-[14.5px] leading-relaxed",
        "data-testid": "rte-content",
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="border border-slate-200 rounded-md p-4 text-[#6B7A8D]">Loading editor…</div>;

  const onPickImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        toast.loading("Uploading image…", { id: "img-up" });
        const res = await uploadImage(file);
        toast.success("Image uploaded", { id: "img-up" });
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
        // We store the image as <img src="...">. For the public site, we'll resolve via the same /api/images/{id}.
        // To embed inline, we need actual image bytes; quickest: load via /api/images/{id} -> data_url.
        const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/images/${res.id}`;
        const r = await fetch(apiUrl);
        const j = await r.json();
        editor.chain().focus().setImage({ src: j.data_url, alt: file.name }).run();
      } catch (e) {
        toast.error("Image upload failed", { id: "img-up" });
      }
    };
    input.click();
  };

  const setLink = () => {
    const url = window.prompt("URL");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <Btn data-testid="rte-h1" title="Heading 1" active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><TextHOne size={16} weight="bold" /></Btn>
        <Btn data-testid="rte-h2" title="Heading 2" active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><TextHTwo size={16} weight="bold" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn data-testid="rte-bold" title="Bold" active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}><TextB size={16} weight="bold" /></Btn>
        <Btn data-testid="rte-italic" title="Italic" active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}><TextItalic size={16} weight="bold" /></Btn>
        <Btn data-testid="rte-bullet" title="Bullet list" active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}><ListBullets size={16} weight="bold" /></Btn>
        <Btn data-testid="rte-ordered" title="Ordered list" active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListNumbers size={16} weight="bold" /></Btn>
        <Btn data-testid="rte-quote" title="Quote" active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quotes size={16} weight="bold" /></Btn>
        <Btn data-testid="rte-link" title="Link" active={editor.isActive("link")} onClick={setLink}>
          <LinkSimple size={16} weight="bold" />
        </Btn>
        <Btn data-testid="rte-image" title="Image" onClick={onPickImage}><ImgIcon size={16} weight="bold" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn data-testid="rte-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}><ArrowUUpLeft size={16} weight="bold" /></Btn>
        <Btn data-testid="rte-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}><ArrowUUpRight size={16} weight="bold" /></Btn>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}
